import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { track } from "../lib/api";

const LOGO_HEADER = `${process.env.REACT_APP_BACKEND_URL}/api/brand/Driver_Reborn_Horizontal_Dark_Transparent.png?v=5`;
const LOGO_FOOTER = `${process.env.REACT_APP_BACKEND_URL}/api/brand/Driver_Reborn_Horizontal_Dark_With_Tagline_Transparent.png?v=5`;
const LOGO_MONOGRAM = `${process.env.REACT_APP_BACKEND_URL}/api/brand/Driver_Reborn_DR_Monogram_Dark_Transparent.png?v=5`;

export const PRIMARY_CTA = "Request a 20-Minute Fleet Owner Conversation";
export const BOOKING_URL = process.env.REACT_APP_BOOKING_URL;

export function BookingLink({ className = "btn-primary", testid, children, onClick }) {
  return (
    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid}
      onClick={() => { track("booking_click", window.location.pathname, { testid }); if (onClick) onClick(); }}>
      {children || PRIMARY_CTA}
    </a>
  );
}

const NAV = [
  { to: "/", label: "Home" },
  { to: "/fleet-crossroads-scorecard", label: "Fleet Crossroads Scorecard" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function Brand({ size = "md" }) {
  // Per official brand rules: desktop 42-52px, mobile 34-42px.
  // Desktop shows full horizontal wordmark; mobile shows the compact DR monogram.
  const desktopH = size === "sm" ? 40 : size === "lg" ? 88 : 48;
  const mobileH = size === "sm" ? 34 : 40;
  return (
    <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo">
      <img
        src={LOGO_MONOGRAM}
        alt="Driver Reborn Advisory"
        style={{ height: mobileH, objectFit: "contain" }}
        className="block md:hidden"
      />
      <img
        src={LOGO_HEADER}
        alt="Driver Reborn Advisory — Clarity Creates Freedom"
        style={{ height: desktopH, objectFit: "contain" }}
        className="hidden md:block"
      />
    </Link>
  );
}

export function Header({ minimal = false }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  if (minimal) {
    return (
      <header className="border-b border-[#2B2B2B] bg-[#0A0A0A]">
        <div className="container-narrow flex items-center justify-between py-4">
          <Brand size="sm" />
        </div>
      </header>
    );
  }
  return (
    <header className="border-b border-[#2B2B2B] bg-[#0A0A0A] sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between gap-4 py-3">
        <Brand />
        <nav className="hidden xl:flex items-center gap-5 ml-6">
          {NAV.map(n => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm uppercase tracking-widest whitespace-nowrap transition-colors ${loc.pathname === n.to ? "text-[#FF6A00]" : "text-[#E6E6E6] hover:text-[#FF6A00]"}`}
            >
              {n.label}
            </Link>
          ))}
          <BookingLink className="btn-primary whitespace-nowrap text-sm" testid="nav-cta-conversation" />
        </nav>
        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-[#E6E6E6]"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>
      {open && (
        <div className="xl:hidden border-t border-[#2B2B2B] bg-[#0A0A0A]">
          <div className="container-narrow py-4 flex flex-col gap-3">
            {NAV.map(n => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base uppercase tracking-wider py-2 border-b border-[#2B2B2B]"
              >
                {n.label}
              </Link>
            ))}
            <BookingLink onClick={() => setOpen(false)} className="btn-primary justify-center text-center mt-2" testid="mobile-cta-conversation" />
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[#2B2B2B] bg-[#0A0A0A] mt-16">
      <div className="container-narrow py-12 grid md:grid-cols-3 gap-8">
        <div>
          <img
            src={LOGO_FOOTER}
            alt="Driver Reborn Advisory — Clarity Creates Freedom"
            style={{ height: 72, objectFit: "contain" }}
            className="mb-4"
          />
          <p className="text-sm text-[#A1A1AA] leading-relaxed">
            Clarity creates freedom. Driver Reborn Advisory helps established fleet owners uncover what is weakening the company, identify the highest-priority problem, and determine the correct next step.
          </p>
        </div>
        <div>
          <div className="font-bebas text-lg text-[#FF6A00] mb-2 tracking-wider">SITE</div>
          <ul className="space-y-1 text-sm">
            <li><Link to="/" data-testid="footer-home">Home</Link></li>
            <li><Link to="/fleet-crossroads-scorecard" data-testid="footer-scorecard">Fleet Crossroads Scorecard</Link></li>
            <li><Link to="/about" data-testid="footer-about">About</Link></li>
            <li><BookingLink className="" testid="footer-conversation">Book a 20-Minute Conversation</BookingLink></li>
            <li><Link to="/apply" data-testid="footer-application">Fleet Owner Application</Link></li>
            <li><Link to="/contact" data-testid="footer-contact">Contact</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-bebas text-lg text-[#FF6A00] mb-2 tracking-wider">LEGAL</div>
          <ul className="space-y-1 text-sm">
            <li><Link to="/legal/privacy" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/legal/terms" data-testid="footer-terms">Terms and Conditions</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#2B2B2B]">
        <div className="container-narrow py-4 text-xs text-[#A1A1AA] flex justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} Driver Reborn Advisory. All rights reserved.</span>
          <span className="text-[#FF6A00] font-bebas tracking-widest">CLARITY CREATES FREEDOM</span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children, minimal = false, hideFooter = false }) {
  return (
    <div className="App min-h-screen flex flex-col">
      <Header minimal={minimal} />
      <main className="flex-1">{children}</main>
      {!hideFooter && <Footer />}
    </div>
  );
}
